import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { ActionBar, Toolbar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Badge,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Disclosure,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useCachePolicies,
  useUpdateCachePolicies,
  useDeleteCachePolicies,
  type CachePolicyRuleWire,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';

// Match-condition fields/operators mirror the Redirects tab — both ride the same
// wirefilter condition struct on the BE ({ field, operator, values }).
const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'url_path', label: 'URL Path' },
  { value: 'url_query', label: 'URL Query' },
  { value: 'request_method', label: 'Request Method' },
  { value: 'hostname', label: 'Hostname' },
  { value: 'user_agent', label: 'User Agent' },
  { value: 'cookie', label: 'Cookie' },
  { value: 'origin', label: 'Origin' },
  { value: 'via', label: 'Via' },
  { value: 'ip_source', label: 'Source IP' },
];
const OPERATOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'regex', label: 'Regex' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'not_starts_with', label: 'Not Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'not_ends_with', label: 'Not Ends With' },
];
const HEADER_ACTIONS = ['set', 'append', 'remove'];

// ── Local (UI) model ──────────────────────────────────────────────────────
interface Condition {
  id: string;
  field: string;
  operator: string;
  values: string[];
}
interface ResponseHeader {
  id: string;
  action: string;
  name: string;
  value: string;
}
interface PolicyRule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  conditions: Condition[];
  noCache: boolean;
  enableXCache: boolean;
  ignoreNoCacheOrigin: boolean;
  ignoreNoCacheClient: boolean;
  disableAutoCompression: boolean;
  ignoreQueryString: boolean;
  edgeTTL: string;
  browserTTL: string;
  staleWhileRevalidate: string;
  staleIfError: string;
  // Cache Key Customization (wire: cache_key)
  queryStrategy: string;
  querySort: boolean;
  keyHeaders: string;
  keyCookies: string;
  keyDeviceType: boolean;
  keyGeo: boolean;
  keyLang: boolean;
  // Vary Header Control (wire: vary)
  varyEnabled: boolean;
  varyNormalizeAE: boolean;
  varyAllowlist: string;
  // Force Vary Headers (wire: vary_headers)
  varyHeaders: string;
  responseHeaders: ResponseHeader[];
}

let _rid = 0;
const localId = () => `cp${++_rid}`;
const csv = (a: unknown): string => (Array.isArray(a) ? (a as unknown[]).map(String).join(', ') : '');
const splitCsv = (s: string): string[] => s.split(',').map((x) => x.trim()).filter(Boolean);

const hydrate = (r: CachePolicyRuleWire): PolicyRule => ({
  id: localId(),
  name: r.name || '',
  priority: r.priority ?? 0,
  enabled: r.enabled ?? true,
  conditions: (r.conditions || []).map((c) => ({
    id: localId(),
    field: c.field || 'url_path',
    operator: c.operator || 'starts_with',
    values: Array.isArray(c.values) ? c.values : [],
  })),
  noCache: r.no_cache ?? false,
  enableXCache: r.enable_x_cache ?? false,
  ignoreNoCacheOrigin: r.ignore_no_cache_origin ?? false,
  ignoreNoCacheClient: r.ignore_no_cache_client ?? false,
  disableAutoCompression: r.disable_auto_compression ?? false,
  ignoreQueryString: r.ignore_query_string ?? false,
  edgeTTL: String(r.edge_ttl_secs ?? 0),
  browserTTL: String(r.browser_ttl_secs ?? 0),
  staleWhileRevalidate: String(r.stale_while_revalidate_secs ?? 0),
  staleIfError: String(r.stale_if_error_secs ?? 0),
  queryStrategy: r.cache_key?.query_strategy || 'all',
  querySort: r.cache_key?.query_sort ?? false,
  keyHeaders: csv(r.cache_key?.header_include),
  keyCookies: csv(r.cache_key?.cookie_include),
  keyDeviceType: r.cache_key?.device_type ?? false,
  keyGeo: r.cache_key?.geo ?? false,
  keyLang: r.cache_key?.lang ?? false,
  varyEnabled: r.vary?.enabled ?? false,
  varyNormalizeAE: r.vary?.normalize_accept_encoding ?? true,
  varyAllowlist: csv(r.vary?.allowlist),
  varyHeaders: Array.isArray(r.vary_headers) ? r.vary_headers.join(', ') : '',
  responseHeaders: (r.response_headers || []).map((h) => ({
    id: localId(),
    action: h.action || 'set',
    name: h.name || '',
    value: h.value || '',
  })),
});

const toWire = (r: PolicyRule): CachePolicyRuleWire => ({
  name: r.name.trim(),
  priority: r.priority,
  enabled: r.enabled,
  conditions: r.conditions.map((c) => ({
    field: c.field,
    operator: c.operator,
    values: c.values.map((v) => v.trim()).filter(Boolean),
  })),
  no_cache: r.noCache,
  enable_x_cache: r.enableXCache,
  ignore_no_cache_origin: r.ignoreNoCacheOrigin,
  ignore_no_cache_client: r.ignoreNoCacheClient,
  disable_auto_compression: r.disableAutoCompression,
  ignore_query_string: r.ignoreQueryString,
  edge_ttl_secs: Number(r.edgeTTL) || 0,
  browser_ttl_secs: Number(r.browserTTL) || 0,
  stale_while_revalidate_secs: Number(r.staleWhileRevalidate) || 0,
  stale_if_error_secs: Number(r.staleIfError) || 0,
  cache_key: {
    query_strategy: r.queryStrategy,
    query_params: [],
    query_sort: r.querySort,
    header_include: splitCsv(r.keyHeaders),
    cookie_include: splitCsv(r.keyCookies),
    device_type: r.keyDeviceType,
    geo: r.keyGeo,
    lang: r.keyLang,
  },
  vary: {
    enabled: r.varyEnabled,
    allowlist: splitCsv(r.varyAllowlist),
    normalize_accept_encoding: r.varyNormalizeAE,
  },
  vary_headers: splitCsv(r.varyHeaders),
  response_headers: r.responseHeaders
    .filter((h) => h.name.trim())
    .map((h) => ({ action: h.action, name: h.name.trim(), value: h.value })),
});

const blankRule = (priority: number): PolicyRule => ({
  id: localId(),
  name: '',
  priority,
  enabled: true,
  conditions: [{ id: localId(), field: 'url_path', operator: 'starts_with', values: [''] }],
  noCache: false,
  enableXCache: false,
  ignoreNoCacheOrigin: false,
  ignoreNoCacheClient: false,
  disableAutoCompression: false,
  ignoreQueryString: false,
  edgeTTL: '0',
  browserTTL: '0',
  staleWhileRevalidate: '0',
  staleIfError: '0',
  queryStrategy: 'all',
  querySort: false,
  keyHeaders: '',
  keyCookies: '',
  keyDeviceType: false,
  keyGeo: false,
  keyLang: false,
  varyEnabled: false,
  varyNormalizeAE: true,
  varyAllowlist: '',
  varyHeaders: '',
  responseHeaders: [],
});

// Behavior chips shown in the table — derived from the rule's enabled flags.
const behaviorBadges = (r: PolicyRule): string[] => {
  const out: string[] = [];
  if (r.noCache) out.push('No Cache');
  if (r.enableXCache) out.push('X-Cache');
  if (r.ignoreQueryString) out.push('Ign QS');
  if (r.disableAutoCompression) out.push('No Compress');
  return out;
};

export function CachePolicyRules({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCachePolicies(domainId);
  const isConfigured = !!cfg;

  const [rules, setRules] = useState<PolicyRule[]>([]);
  const [snapshot, setSnapshot] = useState('');
  const [editing, setEditing] = useState<PolicyRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PolicyRule | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const next = (cfg?.rules ?? []).map(hydrate).sort((a, b) => a.priority - b.priority);
    setRules(next);
    setSnapshot(JSON.stringify(next.map(toWire)));
  }, [cfg]);

  // Compare against the wire shape so local-only ids don't register as dirty.
  const dirty = useMemo(() => JSON.stringify(rules.map(toWire)) !== snapshot, [rules, snapshot]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) => r.name.toLowerCase().includes(q));
  }, [rules, search]);
  const revert = () => setRules((cfg?.rules ?? []).map(hydrate).sort((a, b) => a.priority - b.priority));

  const update = useUpdateCachePolicies(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cache_policy', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Cache policy rules saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save cache policy rules', description: errorMessage(e, 'Could not save cache policy rules') }),
  });
  const remove = useDeleteCachePolicies(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cache_policy', 'delete');
      show({ tone: 'success', title: 'Cache policy rules removed' });
      setRules([]);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove cache policy rules', description: errorMessage(e, 'Could not remove cache policy rules') }),
  });
  const busy = update.isPending || remove.isPending;

  const upsert = (rule: PolicyRule) => {
    setRules((prev) => {
      const idx = prev.findIndex((r) => r.id === rule.id);
      const next = idx > -1 ? prev.map((r) => (r.id === rule.id ? rule : r)) : [...prev, rule];
      return next.sort((a, b) => a.priority - b.priority);
    });
    setEditing(null);
  };

  const toggleEnabled = (id: string, enabled: boolean) =>
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled } : r)));

  const save = () => {
    for (let i = 0; i < rules.length; i++) {
      if (!rules[i].name.trim()) return show({ tone: 'danger', title: `Rule #${i + 1}: name is required` });
    }
    update.mutate(rules.map(toWire));
  };

  const columns = useMemo<ColumnDef<PolicyRule, unknown>[]>(
    () => [
      { id: 'priority', header: 'Priority', size: 72, cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.priority}</Badge> },
      { id: 'name', header: 'Name', size: 150, cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || 'Untitled'}</span> },
      {
        id: 'conditions',
        header: 'Conditions',
        size: 200,
        cell: ({ row }) => {
          const c = row.original.conditions[0];
          const label = FIELD_OPTIONS.find((f) => f.value === c?.field)?.label ?? c?.field;
          const op = OPERATOR_OPTIONS.find((o) => o.value === c?.operator)?.label ?? c?.operator;
          return (
            <span
              style={{
                display: 'block',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--vn-font-size-sm)',
                color: 'var(--vn-fg-muted)',
                fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)',
              }}
            >
              {row.original.conditions.length === 0
                ? 'All requests'
                : `${label} ${op} ${c?.values[0] ?? ''}${row.original.conditions.length > 1 ? ` +${row.original.conditions.length - 1}` : ''}`}
            </span>
          );
        },
      },
      { id: 'ttl', header: 'TTL', size: 64, cell: ({ row }) => <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.edgeTTL}s</span> },
      {
        id: 'behavior',
        header: 'Behavior',
        size: 150,
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
            {behaviorBadges(row.original).map((b) => (
              <Badge key={b} tone="info" appearance="subtle" size="sm">{b}</Badge>
            ))}
          </div>
        ),
      },
      {
        id: 'enabled',
        header: 'Enabled',
        size: 84,
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={row.original.enabled}
              onCheckedChange={(v) => toggleEnabled(row.original.id, v)}
              aria-label="Enabled"
            />
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 96,
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
            <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => setEditing(row.original)}>
              <Pencil size={15} />
            </IconButton>
            <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => setPendingDelete(row.original)}>
              <Trash2 size={15} />
            </IconButton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={() => setEditing(blankRule(rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 10))}
          >
            Add Rule
          </Button>
        }
      >
        <CardTitle>
          Cache Policy Rules{' '}
          <StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>
        </CardTitle>
        <CardDescription>
          Per-request cache overrides for {domain}. Rules are evaluated by priority (lower number = higher
          precedence) and override the global cache settings.
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Toolbar
          start={
            <Input
              placeholder="Search rules…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
          }
        />
        {isLoading ? (
          <Skeleton style={{ height: 120 }} />
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(r) => r.id}
            onRowClick={(r) => setEditing(r)}
            emptyState={
              <EmptyState
                size="sm"
                title={search ? 'No rules match' : 'No cache policy rules'}
                description={search ? 'Try a different search.' : 'Add a rule to override caching for matching requests.'}
                actions={!search ? <Button size="sm" startIcon={<Plus size={14} />} onClick={() => setEditing(blankRule(rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 10))}>Add Rule</Button> : undefined}
              />
            }
          />
        )}

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button variant="secondary" disabled={!dirty || busy} onClick={revert}>
                  Cancel
                </Button>
              )}
              <Button disabled={(isConfigured && !dirty) || busy} onClick={save}>
                {update.isPending ? 'Saving…' : isConfigured ? 'Save' : 'Create'}
              </Button>
            </>
          }
        />
      </CardBody>

      {editing && <PolicyDialog initial={editing} onCancel={() => setEditing(null)} onSave={upsert} />}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete cache policy rule?"
        description={`Remove ${pendingDelete?.name ? `“${pendingDelete.name}”` : 'this rule'}? Save to persist.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) setRules((prev) => prev.filter((r) => r.id !== pendingDelete.id));
          setPendingDelete(null);
        }}
      />
    </Card>
  );
}

function PolicyDialog({
  initial,
  onCancel,
  onSave,
}: {
  initial: PolicyRule;
  onCancel: () => void;
  onSave: (rule: PolicyRule) => void;
}) {
  const [form, setForm] = useState<PolicyRule>(initial);
  const set = <K extends keyof PolicyRule>(k: K, v: PolicyRule[K]) => setForm((f) => ({ ...f, [k]: v }));

  const setCond = (id: string, key: keyof Condition, val: unknown) =>
    set('conditions', form.conditions.map((c) => (c.id === id ? { ...c, [key]: val } : c)));
  const addCond = () =>
    set('conditions', [...form.conditions, { id: localId(), field: 'url_path', operator: 'starts_with', values: [''] }]);
  const removeCond = (id: string) => set('conditions', form.conditions.filter((c) => c.id !== id));

  const setHeader = (id: string, key: keyof ResponseHeader, val: string) =>
    set('responseHeaders', form.responseHeaders.map((h) => (h.id === id ? { ...h, [key]: val } : h)));
  const addHeader = () =>
    set('responseHeaders', [...form.responseHeaders, { id: localId(), action: 'set', name: '', value: '' }]);
  const removeHeader = (id: string) => set('responseHeaders', form.responseHeaders.filter((h) => h.id !== id));

  const behaviorToggles: [keyof PolicyRule, string][] = [
    ['noCache', 'No Cache'],
    ['enableXCache', 'Enable X-Cache Header'],
    ['ignoreNoCacheOrigin', 'Ignore No-Cache from Origin'],
    ['ignoreNoCacheClient', 'Ignore No-Cache from Client'],
    ['disableAutoCompression', 'Disable Auto-Compression'],
    ['ignoreQueryString', 'Ignore Query String'],
  ];
  const ttlFields: [keyof PolicyRule, string, string][] = [
    ['edgeTTL', 'Edge TTL (s)', 'Override edge cache duration. 0 = use origin'],
    ['browserTTL', 'Browser TTL (s)', 'Override browser cache duration. 0 = use origin'],
    ['staleWhileRevalidate', 'Stale-While-Revalidate (s)', 'Serve stale while revalidating. 0 = off'],
    ['staleIfError', 'Stale-If-Error (s)', 'Serve stale on origin error. 0 = off'],
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent style={{ maxWidth: 760 }}>
        <DialogHeader>
          <DialogTitle>{initial.name ? 'Edit Cache Policy Rule' : 'Add Cache Policy Rule'}</DialogTitle>
          <DialogDescription>Match requests and override how the edge caches them.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            {/* Identity */}
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end' }}>
              <Field label="Rule Name" required>
                {(p) => <Input {...p} value={form.name} placeholder="static-assets" onChange={(e) => set('name', e.target.value)} />}
              </Field>
              <Field label="Priority" description="Lower number = higher precedence">
                {(p) => <Input {...p} type="number" value={String(form.priority)} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}
              </Field>
              <Field label="Enabled">
                {() => <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
              </Field>
            </div>

            {/* Match conditions */}
            <div>
              <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>
                Match Conditions <span style={{ fontWeight: 'var(--vn-font-weight-regular)', color: 'var(--vn-fg-muted)' }}>(AND — all must match)</span>
              </div>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {form.conditions.map((c) => (
                  <div key={c.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-start' }}>
                    <Select value={c.field} onValueChange={(v) => setCond(c.id, 'field', v)}>
                      <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={c.operator} onValueChange={(v) => setCond(c.id, 'operator', v)}>
                      <SelectTrigger style={{ flex: 1, minWidth: 130 }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATOR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="One value per line (OR logic)"
                      value={c.values.join('\n')}
                      onChange={(e) => setCond(c.id, 'values', e.target.value.split('\n'))}
                      style={{ flex: 2 }}
                    />
                    <IconButton variant="ghost" size="sm" aria-label="Remove condition" disabled={form.conditions.length === 1} onClick={() => removeCond(c.id)}>
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'var(--vn-space-2)' }}>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addCond}>Add Condition</Button>
              </div>
            </div>

            {/* Cache behavior */}
            <div>
              <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Cache Behavior</div>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                {behaviorToggles.map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
                    <Switch checked={form[k] as boolean} onCheckedChange={(v) => set(k, v as PolicyRule[typeof k])} />
                    <span style={{ fontSize: 'var(--vn-font-size-md)' }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Cache-tuning sections — irrelevant when the rule bypasses cache. */}
            {!form.noCache && (
              <>
                {/* TTL overrides */}
                <div>
                  <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>TTL Overrides</div>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                    {ttlFields.map(([k, label, desc]) => (
                      <Field key={k} label={label} description={desc}>
                        {(p) => <Input {...p} type="number" value={String(form[k])} onChange={(e) => set(k, e.target.value as PolicyRule[typeof k])} />}
                      </Field>
                    ))}
                  </div>
                </div>

                {/* Cache Key Customization (wire: cache_key) */}
                <Disclosure summary="Cache Key Customization">
                  <div style={{ display: 'grid', gap: 'var(--vn-space-4)', paddingTop: 'var(--vn-space-2)' }}>
                    <Field label="Query Strategy">
                      {(p) => (
                        <Select value={form.queryStrategy} onValueChange={(v) => set('queryStrategy', v)}>
                          <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All query params</SelectItem>
                            <SelectItem value="none">Ignore all query params</SelectItem>
                            <SelectItem value="include">Include listed params</SelectItem>
                            <SelectItem value="exclude">Exclude listed params</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
                      <Switch checked={form.querySort} onCheckedChange={(v) => set('querySort', v)} />
                      <span style={{ fontSize: 'var(--vn-font-size-md)' }}>Sort query params</span>
                    </label>
                    <Field label="Include Headers in Key" description="Comma-separated header names">
                      {(p) => <Input {...p} value={form.keyHeaders} placeholder="Accept-Language, X-Custom" onChange={(e) => set('keyHeaders', e.target.value)} />}
                    </Field>
                    <Field label="Include Cookies in Key" description="Comma-separated cookie names">
                      {(p) => <Input {...p} value={form.keyCookies} placeholder="session, locale" onChange={(e) => set('keyCookies', e.target.value)} />}
                    </Field>
                    <div style={{ display: 'flex', gap: 'var(--vn-space-5)', flexWrap: 'wrap' }}>
                      {([['keyDeviceType', 'Device type'], ['keyGeo', 'Geo'], ['keyLang', 'Language']] as [keyof PolicyRule, string][]).map(([k, label]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
                          <Switch checked={form[k] as boolean} onCheckedChange={(v) => set(k, v as PolicyRule[typeof k])} />
                          <span style={{ fontSize: 'var(--vn-font-size-md)' }}>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </Disclosure>

                {/* Vary Header Control (wire: vary) */}
                <Disclosure summary="Vary Header Control">
                  <div style={{ display: 'grid', gap: 'var(--vn-space-4)', paddingTop: 'var(--vn-space-2)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
                      <Switch checked={form.varyEnabled} onCheckedChange={(v) => set('varyEnabled', v)} />
                      <span style={{ fontSize: 'var(--vn-font-size-md)' }}>Enabled</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
                      <Switch checked={form.varyNormalizeAE} onCheckedChange={(v) => set('varyNormalizeAE', v)} />
                      <span style={{ fontSize: 'var(--vn-font-size-md)' }}>Normalize Accept-Encoding</span>
                    </label>
                    <Field label="Vary Allowlist" description="Comma-separated headers allowed in Vary">
                      {(p) => <Input {...p} value={form.varyAllowlist} placeholder="Accept, Accept-Encoding, Accept-Language" onChange={(e) => set('varyAllowlist', e.target.value)} />}
                    </Field>
                  </div>
                </Disclosure>
              </>
            )}

            {/* Force vary headers (wire: vary_headers) */}
            <Field label="Force Vary Headers" description="Comma-separated.">
              {(p) => <Input {...p} value={form.varyHeaders} placeholder="Accept, Accept-Encoding" onChange={(e) => set('varyHeaders', e.target.value)} />}
            </Field>

            {/* Response header overrides */}
            <div>
              <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Response Header Overrides</div>
              {form.responseHeaders.length > 0 && (
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                  {form.responseHeaders.map((h) => (
                    <div key={h.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-start' }}>
                      <Select value={h.action} onValueChange={(v) => setHeader(h.id, 'action', v)}>
                        <SelectTrigger style={{ width: 120 }}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {HEADER_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input placeholder="Header-Name" value={h.name} onChange={(e) => setHeader(h.id, 'name', e.target.value)} style={{ flex: 1 }} />
                      <Input placeholder="Value" value={h.value} disabled={h.action === 'remove'} onChange={(e) => setHeader(h.id, 'value', e.target.value)} style={{ flex: 1 }} />
                      <IconButton variant="ghost" size="sm" aria-label="Remove header" onClick={() => removeHeader(h.id)}>
                        <Trash2 size={15} />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addHeader}>Add Response Header</Button>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button disabled={!form.name.trim()} onClick={() => onSave(form)}>
            {initial.name ? 'Save' : 'Add Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
